/**
 * V108-6-6 — setup warnings, each with an action (no dead ends):
 *   privilege     a connection holds a key broader than read-only and its re-confirmation is due:
 *                 the same PrivilegeConfirm checkbox as at connect time + Re-confirm (owner / admin)
 *   partial_scan  a repository scan stopped early: open the step that finishes it (scan locally)
 *   staging       a connection reads a non-counted environment (preview, doesn't count): connect the
 *                 production one, or say this one is production
 *   stale         a source stopped reporting: Check now (owner / admin), else open the connection
 * Built from what the server already returns (posture connections, setup steps); nothing new collected.
 */
import { useRef, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Button } from '@aigovops/design-system';
import { vendorName } from '@aigovops/core';
import { confirmPostureConnectorPrivilege, listPostureConnectors, runPostureConnector, type PostureConnector } from '../../lib/api';
import { queryKeys } from '../../lib/query-keys';
import { setConnectorEnvironment, type EnablerItem } from '../../lib/setup-api';
import type { SetupWarning } from '../../lib/setup-model';
import { PROVIDER_LABEL, PrivilegeConfirm, connectorHealth, privilegeDue, relativeTime } from '../PostureConnections';
import { connectHref } from './EnablerList';
import { Glyph } from './parts';

const label = (c: Pick<PostureConnector, 'kind'>) => PROVIDER_LABEL[c.kind] ?? vendorName(c.kind);

/** PURE: the warnings for the page (exported for tests: every warning has an action). */
export function setupWarnings(o: { slug: string; enablers: readonly EnablerItem[]; connectors: readonly PostureConnector[]; now?: number }): SetupWarning[] {
  const app = `/w/${o.slug}/app/setup`;
  const out: SetupWarning[] = [];
  for (const c of o.connectors) {
    if (c.privilege && c.status !== 'disabled') {
      const due = privilegeDue(c.privilege, o.now);
      if (due.overdue || due.days <= 7) out.push({ kind: 'privilege', key: `priv-${c.connector_id}`, title: `${label(c)} uses a key broader than read-only`, body: `${due.label}. Confirm it is still needed, or remove the connection.`, action: { label: 'Re-confirm', href: `${app}#posture-${c.connector_id}` } });
    }
    if (connectorHealth(c, { now: o.now }).key === 'stale') out.push({ kind: 'stale', key: `stale-${c.connector_id}`, title: `${label(c)} stopped reporting`, body: `Last read ${relativeTime(c.checks.last_observed_at ?? c.last_run_at, o.now)}. Its objectives stop counting as seen until it reads again.`, action: { label: 'Check now', href: `${app}#posture-${c.connector_id}` } });
  }
  for (const e of o.enablers) {
    if (e.code === 'scan:local' && (e.state === 'suggested' || e.state === 'in_progress')) {
      out.push({ kind: 'partial_scan', key: `scan-${e.id}`, title: 'A repository scan stopped early', body: `${e.subject_key.replace(/^(repo|system):/, '')} has more files than the automatic scan reads. What it found so far is kept.`, action: { label: 'Finish the scan', href: `${app}?step=${e.id}#steps` } });
    }
    const p = e.progress;
    // A step whose only connection is staging is already a failed step in the list (with this fix); not said twice.
    if (p.connector_id && p.environment && p.environment !== 'production' && p.kind && p.state !== 'failed') {
      out.push({ kind: 'staging', key: `env-${p.connector_id}`, title: `${vendorName(p.kind)} reads ${p.environment}${p.target_label ? ` (${p.target_label})` : ''}`, body: 'It shows as a preview and doesn\'t count toward readiness.', action: { label: `Connect ${vendorName(p.kind)} production`, href: connectHref(o.slug, p.kind) } });
    }
  }
  return out;
}

export function SetupWarnings({ slug, enablers, approver, onChanged, toast }: { slug: string; enablers: readonly EnablerItem[]; approver: boolean; onChanged: () => Promise<unknown>; toast: (m: string) => void }) {
  const q = useQuery({ queryKey: queryKeys.postureConnectors(slug), queryFn: () => listPostureConnectors(slug), retry: false, staleTime: 30_000 });
  const connectors = q.data?.connectors ?? [];
  const warnings = setupWarnings({ slug, enablers, connectors });
  if (!warnings.length) return null;
  return (
    <section aria-labelledby="su-warn-h" style={{ display: 'grid', gap: 'var(--sp-2)' }}>
      <h2 id="su-warn-h" className="su-group-h" style={{ margin: 0 }}>Needs attention <span className="su-n">{warnings.length}</span></h2>
      <ul className="su-warnings">
      {warnings.map((w) => {
        const c = connectors.find((x) => w.key.endsWith(x.connector_id));
        const e = enablers.find((x) => w.key === `env-${x.progress.connector_id}`);
        let extra: ReactNode = null;
        if (w.kind === 'privilege' && c && approver) extra = <Reconfirm slug={slug} c={c} onDone={async () => { await q.refetch(); toast(`${label(c)} re-confirmed.`); }} />;
        if (w.kind === 'stale' && c && approver) extra = <Button compact onClick={() => void runPostureConnector(slug, c.connector_id).then(() => q.refetch()).then(() => toast(`Checking ${label(c)} now.`))}>Check now<span className="su-sr">: {label(c)}</span></Button>;
        if (w.kind === 'staging' && e?.progress.connector_id && approver) {
          extra = <><Link className="su-btn" to={w.action.href}>{w.action.label}</Link>
            <Button variant="ghost" compact onClick={() => void setConnectorEnvironment(slug, e.progress.connector_id!, 'production').then(onChanged).then(() => toast('Labelled production. It counts from the next read.'))}>It is production</Button></>;
        }
        return (
          <li key={w.key} className="su-warn" data-warning={w.kind}>
            <Glyph kind="alert" />
            <div style={{ display: 'grid', gap: 'var(--sp-2)', minWidth: 0 }}>
              <p style={{ margin: 0 }}><strong>{w.title}.</strong> {w.body}</p>
              <div className="su-acts">{extra ?? <Link className="su-btn" to={w.action.href}>{w.action.label}</Link>}</div>
            </div>
          </li>
        );
      })}
      </ul>
    </section>
  );
}

function Reconfirm({ slug, c, onDone }: { slug: string; c: PostureConnector; onDone: () => Promise<void> }) {
  const [ok, setOk] = useState(false);
  const [error, setError] = useState<string | undefined>();
  const [busy, setBusy] = useState(false);
  const box = useRef<HTMLInputElement>(null);
  const submit = async () => {
    if (!ok) { setError('Tick the box to confirm.'); box.current?.focus(); return; }
    setBusy(true);
    try { await confirmPostureConnectorPrivilege(slug, c.connector_id); await onDone(); }
    catch (err) { setError(err instanceof Error ? err.message : 'That did not save. Try again.'); }
    finally { setBusy(false); }
  };
  return <div style={{ display: 'grid', gap: 'var(--sp-2)', flex: '1 1 100%' }}>
    <PrivilegeConfirm id={`reconfirm-${c.connector_id}`} kind={c.kind} privilege={{ ...c.privilege!, beyondReadOnly: c.privilege!.beyondReadOnly ?? [] } as never} checked={ok} error={error} inputRef={box} onChange={(v) => { setOk(v); setError(undefined); }} />
    <div><Button onClick={() => void submit()} disabled={busy}>{busy ? 'Saving…' : 'Re-confirm'}</Button></div>
  </div>;
}
